import { ChevronDown, ChevronRight, Pencil } from "lucide-react";
import React, { useEffect, useState } from "react";

interface UIEditSectionProps {
  title: string;
  icon?: React.ReactNode;
  summary: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  collapseKey?: number;
}

export const UIEditSection = React.memo<UIEditSectionProps>(
  function UIEditSection({
    title,
    icon,
    summary,
    children,
    defaultExpanded = false,
    collapseKey,
  }) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    useEffect(() => {
      if (collapseKey !== undefined && collapseKey > 0) {
        setIsExpanded(false);
      }
    }, [collapseKey]);

    return (
      <div className="bg-card rounded-lg overflow-hidden border border-border">
        {/* Header - always visible */}
        <div
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
            {icon}
            <h3 className="text-sm font-medium text-foreground">{title}</h3>
          </div>
          {!isExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
              className="p-1 hover:bg-muted rounded"
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>

        {/* Summary - shown when collapsed */}
        {!isExpanded && (
          <div className="px-4 py-3">
            <div className="text-xs text-muted-foreground">{summary}</div>
          </div>
        )}

        {/* Expanded content */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-border pt-4">
            {children}
          </div>
        )}
      </div>
    );
  }
);
